import {
  applyColorsToFields,
  DASHBOARD_NAME_ISLAND_ID,
  DashboardBuilderChartWidget,
  DashboardBuilderHeaderWidget,
  DashboardBuilderPowerBiIframeWidget,
  DashboardBuilderNoteWidget,
  DashboardTrackingEventsNames,
  type LoadedDashboard,
  WidgetError,
  widgetOperations,
  WidgetWarning,
} from '@ardoq/dashboard';
import { WidgetDataSourceTypes, WidgetTypes } from '@ardoq/api-types';
import {
  getUpdatedWidgets,
  getWidgetTypeLayoutFieldAggregateAndSort,
  setDefaultAggregateOnCategoricalCharts,
} from './stateUtils';
import {
  widgetFiltersAppliedOnDrawerSave,
  clearDashboardData,
  editWidgetLinkModeDashboardFilterBinding,
  copyWidget,
  CopyWidgetPayload,
  createWidget,
  CreateWidgetPayload,
  DashboardWidgetFilterPayload,
  deleteWidget,
  selectDataSource,
  SelectDataSourcePayload,
  selectField,
  SelectFieldPayload,
  selectWidget,
  selectWidgetType,
  SelectWidgetTypePayload,
  setAvailableDataSources,
  setDashboardWarnings,
  setLoadedDashboard,
  setWidgets,
  setWidgetsErrors,
  setWidgetsPreview,
  updateWidget,
  UpdateWidgetPayload,
  SetWidgetsPreviewPayload,
  setLinkModeDashboardFilterParameterId,
  setPendingConditionSelection,
  clearPendingConditionSelection,
  confirmPendingConditionSelection,
} from './actions';
import { reducedStream, reducer, streamReducer } from '@ardoq/rxbeach';
import { setStateProperty } from '@ardoq/common-helpers';
import {
  getAvailableFieldsForDataSource,
  getSelectedChartWidget,
  isSortableColumInTableWidget,
} from '../utils';
import { constant, isEmpty, omit } from 'lodash';
import { keepValidFilters } from '@ardoq/report-reader';
import { trackEvent } from '../../../tracking/tracking';
import { isWidgetTypeValidForSelectedFieldsAndDatasource } from '../widgetTypeValidation';
import { qualitativeTheme } from '@ardoq/color-picker';
import { selectedDashboardColorPalette$ } from '../../DashboardColorThemeSettings/dashboardColorThemes$';
import { colorSettingsReducers } from './colorSettingsReducers';
import { dashboardFiltersReducers } from './dashboardFiltersReducers';
import { conditionOperations } from '../sidebars/sections/conditionOperations';
import { type Widgets$State } from './types';
import { type PendingConditionSelection } from '../types';

const createNewWidgetFromViewTypeAndLayout = (
  { widgets, ...state }: Widgets$State,
  { viewType, layout, newWidgetId }: CreateWidgetPayload
): Widgets$State => {
  const commonConfig = {
    isUnsaved: true,
    isLoading: false,
    _id: newWidgetId,
    layout: { ...layout, i: newWidgetId },
  };
  const newWidget =
    viewType === WidgetTypes.HEADER
      ? ({
          ...commonConfig,
          content: '',
          viewType,
        } satisfies DashboardBuilderHeaderWidget)
      : viewType === WidgetTypes.NOTE
        ? ({
            ...commonConfig,
            viewType,
            content: '',
          } satisfies DashboardBuilderNoteWidget)
        : viewType === WidgetTypes.POWER_BI_IFRAME
          ? ({
              ...commonConfig,
              viewType,
              url: '',
            } satisfies DashboardBuilderPowerBiIframeWidget)
          : ({
              ...commonConfig,
              name: '',
              fields: [],
              permissions: { canEditWidget: true, canExploreDatasource: true },
              validationProblems: { widgetError: WidgetError.EMPTY_WIDGET },
            } satisfies DashboardBuilderChartWidget);
  return {
    ...state,
    widgets: [...widgets, newWidget],
    selectedWidgetId: newWidgetId,
  };
};

const copyWidgetAndAddToState = (
  state: Widgets$State,
  { widgetId, layout, newWidgetId, shouldSetAsSelected }: CopyWidgetPayload
): Widgets$State => {
  const copiedWidget = state.widgets.find(widget => widget._id === widgetId);
  if (!copiedWidget) return state;

  return {
    ...state,
    selectedWidgetId: shouldSetAsSelected ? newWidgetId : null, // dont open sidebar if global filters are applied
    widgets: [
      ...state.widgets,
      {
        ...copiedWidget,
        globalFilterBindings: undefined, // not copy global filter bindings
        _id: newWidgetId,
        isUnsaved: true,
        layout: { ...layout, h: copiedWidget.layout.h, i: newWidgetId },
        ...(widgetOperations.isChartWidgetData(copiedWidget)
          ? { name: `${copiedWidget.name ?? ''} (copy)` }
          : widgetOperations.isPowerBiIframeWidgetData(copiedWidget)
            ? {}
            : { content: `${copiedWidget.content} (copy)` }),
      },
    ],
    settings: {
      ...state.settings,
      colors: state.settings.colors.map(color => ({
        ...color,
        widgetsUsingThis: [
          ...color.widgetsUsingThis,
          ...(color.widgetsUsingThis.includes(widgetId) ? [newWidgetId] : []),
        ],
      })),
    },
  };
};

const removeDeletedWidget = (
  { widgets, ...state }: Widgets$State,
  deletedWidgetId: string
): Widgets$State => ({
  ...state,
  widgets: widgets.filter(widget => widget._id !== deletedWidgetId),
  selectedWidgetId:
    state.selectedWidgetId === deletedWidgetId ? null : state.selectedWidgetId,
});

const updateWidgetAttributes = (
  { widgets, ...state }: Widgets$State,
  updatedAttributes: UpdateWidgetPayload
): Widgets$State => ({
  ...state,
  widgets: getUpdatedWidgets(widgets, [updatedAttributes]),
});

const setDataSource = (
  { widgets, ...state }: Widgets$State,
  { widgetId, selectedDataSourceEntity }: SelectDataSourcePayload
): Widgets$State => {
  const selectedWidget = getSelectedChartWidget(widgets, widgetId);
  const availableFieldsForDataSource = getAvailableFieldsForDataSource(
    state.availableDataSources,
    selectedDataSourceEntity
  );
  const datasource = {
    sourceType: selectedDataSourceEntity.sourceType,
    sourceId: selectedDataSourceEntity.sourceId,
  };
  return {
    ...state,
    pendingConditionSelection: undefined,
    widgets: getUpdatedWidgets(widgets, [
      {
        datasource,
        _id: selectedWidget._id,
        condition: undefined,
        ...getWidgetTypeLayoutFieldAggregateAndSort(
          {
            ...selectedWidget,
            fields:
              datasource.sourceType === WidgetDataSourceTypes.SURVEY &&
              selectedWidget.fields.length > 1
                ? selectedWidget.fields.slice(0, 1)
                : selectedWidget.fields,
            datasource,
            filters: undefined,
            condition: undefined,
          },
          availableFieldsForDataSource
        ),
      },
    ]),
  };
};

const setSelectedFields = (
  { widgets, availableDataSources, ...state }: Widgets$State,
  { dataSourceFields, widgetId }: SelectFieldPayload
): Widgets$State => {
  const selectedWidget = getSelectedChartWidget(widgets, widgetId);
  const availableFieldsForDataSource = getAvailableFieldsForDataSource(
    availableDataSources,
    selectedWidget?.datasource
  );
  const newWidgetData = {
    datasource: selectedWidget.datasource,
    _id: selectedWidget._id,
    ...getWidgetTypeLayoutFieldAggregateAndSort(
      {
        ...selectedWidget,
        fields: dataSourceFields.map(field => field.name),
      },
      availableFieldsForDataSource
    ),
  };
  const isTableWidgetData = widgetOperations.isTableWidgetData(newWidgetData);
  const shouldClearTableWidgetData = // if selected fields are cleared we can't get a preview for the table widget so we have to manually remove the preview data
    isTableWidgetData &&
    !dataSourceFields.length &&
    selectedWidget.fields.length;

  const sort = isTableWidgetData
    ? {
        sort: widgetOperations.maybeResetSortingIfPossible(
          newWidgetData,
          availableFieldsForDataSource.filter(isSortableColumInTableWidget)
        ),
      }
    : {};

  return {
    ...state,
    availableDataSources,
    widgets: getUpdatedWidgets(widgets, [
      {
        ...newWidgetData,
        ...(shouldClearTableWidgetData
          ? {
              data: undefined,
              tableFields: undefined,
              scopeData: undefined,
            }
          : {}),
        ...sort,
        _id: widgetId,
      },
    ]),
  };
};

const setWidgetFilters = (
  { widgets, availableDataSources, ...state }: Widgets$State,
  { filters, columns, widgetId }: DashboardWidgetFilterPayload
): Widgets$State => {
  const selectedWidget = getSelectedChartWidget(widgets, widgetId);
  const availableFieldsForDataSource = getAvailableFieldsForDataSource(
    availableDataSources,
    selectedWidget.datasource
  );

  const validatedFilters = !isEmpty(filters)
    ? (keepValidFilters(filters, columns) ?? undefined)
    : undefined;

  const newWidgetData = {
    datasource: selectedWidget.datasource,
    _id: selectedWidget._id,
    ...getWidgetTypeLayoutFieldAggregateAndSort(
      {
        ...selectedWidget,
        filters: validatedFilters,
      },
      availableFieldsForDataSource
    ),
  };

  trackEvent(DashboardTrackingEventsNames.APPLIED_WIDGET_FILTER, {
    numberOfFilters: validatedFilters
      ? Object.keys(validatedFilters).length
      : 0,
  });

  return {
    ...state,
    availableDataSources,
    widgets: getUpdatedWidgets(widgets, [
      {
        ...newWidgetData,
        _id: widgetId,
      },
    ]),
  };
};

const setWarningsOnWidgets = ({
  widgets,
  availableDataSources,
  ...state
}: Widgets$State): Widgets$State => ({
  ...state,
  availableDataSources,
  widgets: widgets.map(w => {
    const availableFieldsForDataSource = widgetOperations.isChartWidgetData(w)
      ? getAvailableFieldsForDataSource(availableDataSources, w.datasource)
      : [];
    if (
      widgetOperations.isChartWidgetData(w) &&
      w.viewType &&
      (w.permissions?.canEditWidget ?? false) && // If the user can't edit the widget, they don't have any available fields, so we test the normal validation criteria
      !w.isUnsaved &&
      !w.error &&
      !isWidgetTypeValidForSelectedFieldsAndDatasource({
        widgetType: w.viewType,
        datasource: w.datasource,
        availableFieldsForDataSource: availableFieldsForDataSource,
        selectedFields: availableFieldsForDataSource.filter(field =>
          w.fields.includes(field.name)
        ),
      })
    ) {
      return {
        ...w,
        warning: WidgetWarning.INCOMPATIBLE_WIDGET_TYPE_WITH_DATA_SOURCE,
      };
    }
    return w;
  }),
});

const getConditionConflictOnWidgetTypeSwitch = (
  newWidgetType: WidgetTypes,
  selectedWidget: DashboardBuilderChartWidget
): PendingConditionSelection | undefined => {
  if (newWidgetType !== WidgetTypes.PERCENTAGE) return undefined;

  // can't use widgetOperations.isPercentageChartData since viewType hasn't changed yet
  if (!('condition' in selectedWidget) || !selectedWidget.condition)
    return undefined;

  const conditionData = conditionOperations.apiFilterToCondition(
    selectedWidget.condition
  );
  if (!conditionData) return undefined;

  const conflictType = conditionOperations.getConflictType(
    conditionData.columnKey,
    selectedWidget.filters,
    selectedWidget.globalFilterBindings
  );
  if (!conflictType) return undefined;

  return {
    widgetId: selectedWidget._id,
    columnKey: conditionData.columnKey,
    conflictType,
  };
};

const setWidgetType = (
  { widgets, availableDataSources, ...state }: Widgets$State,
  { widgetId, widgetType }: SelectWidgetTypePayload
): Widgets$State => {
  const selectedWidget = getSelectedChartWidget(widgets, widgetId);
  const availableFieldsForDataSource = getAvailableFieldsForDataSource(
    availableDataSources,
    selectedWidget?.datasource
  );

  const newWidgetData = {
    datasource: selectedWidget.datasource,
    _id: selectedWidget._id,
    ...getWidgetTypeLayoutFieldAggregateAndSort(
      {
        ...selectedWidget,
        viewType: widgetType,
        filters:
          selectedWidget.viewType !== widgetType &&
          [WidgetTypes.LINE_CHART, WidgetTypes.STACKED_BAR_CHART].includes(
            widgetType // timeline widgets don't support filtering
          )
            ? undefined
            : selectedWidget.filters,
      },
      availableFieldsForDataSource,
      true
    ),
    data: null,
    globalFilterBindings: selectedWidget.globalFilterBindings,
  };

  if (!widgetOperations.isChartTypeSupportDashboardFilters(newWidgetData)) {
    newWidgetData.globalFilterBindings = undefined;
  }

  const pendingConditionSelection = getConditionConflictOnWidgetTypeSwitch(
    widgetType, // the resolved widget type from newWidgetData.viewType
    selectedWidget
  );

  return {
    ...state,
    availableDataSources,
    widgets: getUpdatedWidgets(widgets, [newWidgetData]),
    ...(pendingConditionSelection !== undefined
      ? { pendingConditionSelection }
      : {}),
  };
};

const setWidgetsErrorsAndIsLoading = (
  { widgets, ...state }: Widgets$State,
  widgetErrors: Array<{ _id: string; error: string }>
): Widgets$State => ({
  ...state,
  widgets: getUpdatedWidgets(
    widgets,
    widgetErrors.map(error => ({
      ...error,
      isLoading: false,
    }))
  ),
});

const setLoadedWidgets = (
  state: Widgets$State,
  loadedDashboard: LoadedDashboard
): Widgets$State => ({
  ...state,
  selectedWidgetId: null,
  widgets: loadedDashboard.widgets.map(setDefaultAggregateOnCategoricalCharts),
  settings: loadedDashboard.settings,
  globalFilterValues: loadedDashboard.globalFilterValues ?? {},
  globalFilterParameters: loadedDashboard.globalFilterParameters,
});

const handleSetWidgetsPreview = (
  state: Widgets$State,
  { previewWidgets, hasActiveDashboardFilters }: SetWidgetsPreviewPayload
): Widgets$State => {
  const previewWidgetData = previewWidgets.map(previewWidget => {
    const previewWidgetData = widgetOperations.isReadTableWidgetData(
      previewWidget
    )
      ? {
          data: previewWidget.data,
          scopeData: previewWidget.scopeData,
          tableFields: previewWidget.tableFields,
          aggregates: previewWidget.aggregates,
          totalNumberOfRows: previewWidget.totalNumberOfRows,
          globalFilterWarnings: previewWidget.globalFilterWarnings,
        }
      : widgetOperations.isReadNumberWidgetData(previewWidget)
        ? {
            data: previewWidget.data,
            numberFormatOptions: previewWidget.numberFormatOptions,
            trend: previewWidget.trend,
            globalFilterWarnings: previewWidget.globalFilterWarnings,
          }
        : widgetOperations.isCategoricalChartData(previewWidget)
          ? {
              data: previewWidget.data,
              numberFormatOptions: previewWidget.numberFormatOptions,
              globalFilterWarnings: previewWidget.globalFilterWarnings,
            }
          : { data: previewWidget.data };

    return {
      ...previewWidgetData,
      error: undefined,
      isLoading: false,
      _id: previewWidget._id,
    };
  });

  const widgets = getUpdatedWidgets(state.widgets, previewWidgetData);

  return {
    ...state,
    widgets,
    settings: {
      ...state.settings,
      // in the current version, no colors change would happen when there is applied globalFilterValues
      // since edit widgets are disabled when there is applied globalFilterValues
      // this is for making sure no manually assigned colors get lost due to applied dashboard filters
      colors: hasActiveDashboardFilters
        ? state.settings.colors
        : applyColorsToFields({
            widgets,
            colors: state.settings.colors,
            paletteColors: state.selectedColorPalette,
            rememberedColors: {},
          }),
    },
  };
};

const handleClearPendingConditionSelection = (
  state: Widgets$State
): Widgets$State => {
  const { pendingConditionSelection, widgets } = state;
  if (!pendingConditionSelection)
    return { ...state, pendingConditionSelection: undefined };

  return {
    ...state,
    pendingConditionSelection: undefined,
    widgets: getUpdatedWidgets(widgets, [
      { _id: pendingConditionSelection.widgetId, condition: undefined },
    ]),
  };
};

const clearIncompleteCondition = (state: Widgets$State): Widgets$State => {
  const { selectedWidgetId, widgets, availableDataSources } = state;
  // Pending selection is always cleared when sidebar closes
  if (!selectedWidgetId)
    return { ...state, pendingConditionSelection: undefined };

  const selectedWidget = widgets.find(w => w._id === selectedWidgetId);
  // Only percentage charts can have conditions
  if (
    !selectedWidget ||
    !widgetOperations.isPercentageChartData(selectedWidget) ||
    !selectedWidget.condition
  )
    return { ...state, pendingConditionSelection: undefined };

  const columns = getAvailableFieldsForDataSource(
    availableDataSources,
    selectedWidget.datasource
  ).map(conditionOperations.dataSourceFieldToReportColumn);

  // Valid condition can remain; field deleted or type changed clears the condition
  if (keepValidFilters(selectedWidget.condition, columns))
    return { ...state, pendingConditionSelection: undefined };

  // Clear condition (field deleted, type changed, or incomplete)
  return {
    ...state,
    pendingConditionSelection: undefined,
    widgets: getUpdatedWidgets(widgets, [
      { _id: selectedWidgetId, condition: undefined },
    ]),
  };
};

const handleSetLinkModeDashboardFilterParameterId = (
  state: Widgets$State
): Widgets$State => ({
  ...clearIncompleteCondition(state),
  selectedWidgetId: null,
});

const handleSetPendingConditionSelection = (
  state: Widgets$State,
  pendingConditionSelection: PendingConditionSelection
): Widgets$State => ({ ...state, pendingConditionSelection });

const handleConfirmPendingConditionSelection = (
  state: Widgets$State
): Widgets$State => {
  const { pendingConditionSelection, widgets } = state;
  if (!pendingConditionSelection) return state;

  const { widgetId, columnKey, conflictType } = pendingConditionSelection;
  const widget = getSelectedChartWidget(widgets, widgetId);
  // represent condition as a filter
  const condition = { [columnKey]: { values: [] } };

  if (conflictType === 'chartFilter') {
    return {
      ...state,
      pendingConditionSelection: undefined,
      widgets: getUpdatedWidgets(widgets, [
        {
          _id: widgetId,
          filters: widget.filters
            ? conditionOperations.removeReportColumnFromFilters(
                widget.filters,
                columnKey
              )
            : undefined,
          condition,
        },
      ]),
    };
  }

  const remainingBindings = omit(widget.globalFilterBindings ?? {}, columnKey);

  return {
    ...state,
    pendingConditionSelection: undefined,
    widgets: getUpdatedWidgets(widgets, [
      {
        _id: widgetId,
        globalFilterBindings: isEmpty(remainingBindings)
          ? undefined
          : remainingBindings,
        condition,
      },
    ]),
  };
};

const handleSelectWidget = (
  state: Widgets$State,
  selectedWidgetId: string | null
): Widgets$State => {
  if (selectedWidgetId !== null) return { ...state, selectedWidgetId };
  return { ...clearIncompleteCondition(state), selectedWidgetId: null };
};

const defaultState: Widgets$State = {
  widgets: [],
  availableDataSources: [],
  selectedWidgetId: DASHBOARD_NAME_ISLAND_ID,
  settings: { colors: [] },
  selectedColorPalette: qualitativeTheme.paletteColors,
  globalFilterParameters: [],
  globalFilterValues: {},
  pendingConditionSelection: undefined,
};

export const widgetsAndSettings$ = reducedStream<Widgets$State>(
  'widgetsAndSettings$',
  defaultState,
  [
    reducer(clearDashboardData, constant(defaultState)),
    reducer(copyWidget, copyWidgetAndAddToState),
    reducer(createWidget, createNewWidgetFromViewTypeAndLayout),
    reducer(deleteWidget, removeDeletedWidget),
    reducer(updateWidget, updateWidgetAttributes),
    reducer(selectDataSource, setDataSource),
    reducer(selectField, setSelectedFields),
    reducer(widgetFiltersAppliedOnDrawerSave, setWidgetFilters),
    reducer(setDashboardWarnings, setWarningsOnWidgets),
    reducer(selectWidgetType, setWidgetType),
    reducer(setWidgetsErrors, setWidgetsErrorsAndIsLoading),
    reducer(setLoadedDashboard, setLoadedWidgets),
    reducer(setWidgetsPreview, handleSetWidgetsPreview),
    reducer(setAvailableDataSources, setStateProperty('availableDataSources')),
    reducer(setWidgets, setStateProperty('widgets')),
    reducer(selectWidget, handleSelectWidget),
    reducer(
      setLinkModeDashboardFilterParameterId,
      handleSetLinkModeDashboardFilterParameterId
    ),
    reducer(
      editWidgetLinkModeDashboardFilterBinding,
      setStateProperty('selectedWidgetId')
    ),
    streamReducer(
      selectedDashboardColorPalette$,
      setStateProperty('selectedColorPalette')
    ),
    reducer(setPendingConditionSelection, handleSetPendingConditionSelection),
    reducer(
      clearPendingConditionSelection,
      handleClearPendingConditionSelection
    ),
    reducer(
      confirmPendingConditionSelection,
      handleConfirmPendingConditionSelection
    ),
    ...colorSettingsReducers,
    ...dashboardFiltersReducers,
  ]
);
